import React, { useState } from "react";
import {
  Github,
  Linkedin,
  Mail,
  MapPin,
  ExternalLink,
  Code,
  Briefcase,
  User,
  Terminal,
} from "lucide-react";
import { motion } from "framer-motion";

// --- PROFILE DATA (EDIT THIS SECTION) ---
// const profileData = {
//   name: "Gitesh Kumar",
//   role: "Software Engineer",
//   headline: "Java Full Stack | Python/Django | C++ | Problem Solver",
//   location: "Gurugram, Haryana, India",
//   email: "contact@giteshkumar.dev", // Update this
//   linkedin: "https://www.linkedin.com/in/giteshkumar9/",
//   github: "https://github.com/", // Update this
//   about: `I am a dedicated Software Engineer with over 2.5 years of experience, currently working at Cognizant. I specialize in Java Full-Stack development, Python/Django, and C++. I thrive on solving complex algorithmic problems and building scalable, impactful solutions. I am passionate about driving innovation and continuously learning new technologies.`,
//   skills: [
//     "Java",
//     "Python",
//     "Django",
//     "C++",
//     "React.js",
//     "JavaScript",
//     "SQL",
//     "Data Structures",
//     "Algorithms",
//     "Git",
//   ],
//   experience: [
//     {
//       company: "Cognizant",
//       role: "Software Engineer (Associate)",
//       duration: "2021 - Present",
//       description:
//         "Specializing in full-stack development using Java and Python. delivering high-quality software solutions and resolving complex technical issues.",
//     },
//     // Add more experience here if needed
//   ],
//   projects: [
//     {
//       title: "Portfolio Website",
//       tech: "React, Tailwind, Framer Motion",
//       description:
//         "A personal portfolio website to showcase my skills and professional journey.",
//       link: "#",
//     },
//     {
//       title: "Full Stack Application",
//       tech: "Java, Spring Boot, React",
//       description:
//         "Developed a scalable web application handling complex data processing tasks.",
//       link: "#",
//     },
//   ],
// };

// --- PROFILE DATA (UPDATED FROM RESUME) ---
const profileData = {
  name: "Gitesh Kumar",
  role: "Software Engineer",
  headline:
    "Software Engineer @ SITA | Java (8/11/17) | Spring Boot | Microservices | Docker | CI/CD",
  location: "Gurugram, Haryana, India",
  email: "gitesh.kumar2503@gmail.com",
  linkedin: "https://www.linkedin.com/in/giteshkumar9",
  github: "https://github.com/", // Add your GitHub link if available
  about: `I am a passionate Software Engineer with 4 years of experience crafting robust backend solutions and optimizing system performance. Currently working at SITA, I specialize in building scalable travel and border security solutions. My expertise spans Core Java, Spring Boot, Microservices, and Cloud technologies like AWS and Azure DevOps. I thrive on solving complex algorithmic problems and using design patterns to write clean, maintainable code[cite: 20].`,
  skills: [
    "Java 8/11/17",
    "Spring Boot",
    "Microservices",
    "RESTful APIs",
    "Redis",
    "Docker",
    "AWS",
    "Azure DevOps",
    "SQL (MySQL)",
    "MongoDB",
    "Python",
    "Data Structures",
  ],
  experience: [
    {
      company: "SITA",
      role: "Software Engineer",
      duration: "Aug 2025 - Present",
      description:
        "Building scalable Travel & Border Security solutions. Leveraging cutting-edge technologies to build high-quality, impactful applications.",
    },
    {
      company: "Cognizant",
      role: "Software Engineer",
      duration: "Jan 2024 - July 2025",
      description:
        "Developed RESTful APIs for a high-traffic Employee Management Portal (20k+ users). Implemented JWT authentication, Redis caching (20% reduced latency), and automated leave management integrations.",
    },
    {
      company: "Cognizant",
      role: "Junior Software Engineer",
      duration: "Oct 2022 - Jan 2024",
      description:
        "Enhanced operational efficiency by creating a Python automation tool (Tkinter/Pandas) that reduced processing time by 70%. Led a seamless MySQL server upgrade project.",
    },
    {
      company: "Cognizant",
      role: "Programmer Analyst Trainee",
      duration: "Oct 2021 - Oct 2022",
      description:
        "Completed comprehensive Java Full Stack training. Worked with Servlets, JSP, and React to build dynamic interfaces and robust server-side applications.",
    },
  ],
  projects: [
    {
      title: "Employee Management Portal",
      tech: "Spring Boot, JWT, Redis, JUnit5",
      description:
        "A high-traffic portal for 20,000+ users handling time tracking and shift scheduling. Reduced server response times by 20% using Redis caching.",
      link: "#",
    },
    {
      title: "Python Automation Tool",
      tech: "Python, Tkinter, Pandas",
      description:
        "Desktop automation tool to streamline record retrieval from server files, achieving a 70% reduction in manual processing time.",
      link: "#",
    },
    {
      title: "Admin Platform (Internship)",
      tech: "Django, AWS, Docker",
      description:
        "Contributed to a Django-based fullstack web application and developed an Admin platform for browser-based access to AWS VMs.",
      link: "#",
    },
  ],
};
// --- COMPONENTS ---

const Section = ({ children, id, className = "" }) => (
  <section id={id} className={`py-20 px-6 md:px-20 ${className}`}>
    {children}
  </section>
);

const Card = ({ children, className = "" }) => (
  <motion.div
    whileHover={{ y: -5 }}
    className={`bg-gray-800 p-6 rounded-xl border border-gray-700 shadow-lg ${className}`}
  >
    {children}
  </motion.div>
);

export default function Portfolio() {
  const [activeTab, setActiveTab] = useState("about");

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 font-sans selection:bg-blue-500 selection:text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-gray-900/90 backdrop-blur-sm border-b border-gray-800 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
            GK.
          </span>
          <div className="hidden md:flex gap-8 text-sm font-medium text-gray-400">
            {["About", "Experience", "Projects", "Contact"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="hover:text-blue-400 transition-colors"
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <Section className="pt-32 flex flex-col items-start justify-center min-h-[80vh]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="text-blue-400 font-medium tracking-wide">
            Hello this is a test Portfolio, my name is
          </span>
          <h1 className="text-5xl md:text-7xl font-bold mt-4 text-white">
            {profileData.name}
          </h1>
          <h2 className="text-4xl md:text-6xl font-bold mt-2 text-gray-400">
            I build things for the web.
          </h2>
          <p className="mt-6 max-w-xl text-gray-400 text-lg leading-relaxed">
            {profileData.headline}
          </p>

          <div className="mt-10 flex gap-4">
            <a
              href={profileData.linkedin}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-all"
            >
              <Linkedin size={20} />
              LinkedIn
            </a>
            <a
              href="#contact"
              className="flex items-center gap-2 px-6 py-3 border border-gray-600 hover:border-blue-400 hover:text-blue-400 rounded-lg font-medium transition-all"
            >
              Contact Me
            </a>
          </div>
        </motion.div>
      </Section>

      {/* About & Skills */}
      <Section id="about" className="bg-gray-800/30">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="flex items-center gap-2 text-2xl font-bold text-white mb-6">
              <User className="text-blue-400" /> About Me
            </h3>
            <p className="text-gray-400 leading-relaxed mb-6">
              {profileData.about}
            </p>
            <div className="flex items-center gap-2 text-gray-400">
              <MapPin size={18} className="text-blue-400" />
              {profileData.location}
            </div>
          </div>

          <div>
            <h3 className="flex items-center gap-2 text-2xl font-bold text-white mb-6">
              <Code className="text-blue-400" /> Technical Skills
            </h3>
            <div className="flex flex-wrap gap-3">
              {profileData.skills.map((skill) => (
                <span
                  key={skill}
                  className="px-3 py-1 bg-blue-900/30 text-blue-300 rounded-full text-sm border border-blue-900/50"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Experience */}
      <Section id="experience">
        <div className="max-w-4xl mx-auto">
          <h3 className="flex items-center gap-2 text-3xl font-bold text-white mb-10">
            <Briefcase className="text-blue-400" /> Where I've Worked
          </h3>
          <div className="space-y-8">
            {profileData.experience.map((exp, index) => (
              <div
                key={index}
                className="relative pl-8 border-l border-gray-700"
              >
                <span className="absolute -left-[5px] top-2 w-2.5 h-2.5 bg-blue-500 rounded-full"></span>
                <h4 className="text-xl font-semibold text-white">{exp.role}</h4>
                <div className="text-blue-400 font-medium mb-1">
                  {exp.company}
                </div>
                <div className="text-sm text-gray-500 mb-4">{exp.duration}</div>
                <p className="text-gray-400">{exp.description}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Projects */}
      <Section id="projects" className="bg-gray-800/30">
        <div className="max-w-6xl mx-auto">
          <h3 className="flex items-center gap-2 text-3xl font-bold text-white mb-10">
            <Terminal className="text-blue-400" /> Featured Projects
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profileData.projects.map((project, index) => (
              <Card key={index}>
                <div className="flex justify-between items-start mb-4">
                  <div className="text-blue-400">
                    <Code size={30} />
                  </div>
                  <a
                    href={project.link}
                    className="text-gray-400 hover:text-white"
                  >
                    <ExternalLink size={20} />
                  </a>
                </div>
                <h4 className="text-xl font-bold text-white mb-2">
                  {project.title}
                </h4>
                <p className="text-gray-400 text-sm mb-4 h-20 overflow-hidden">
                  {project.description}
                </p>
                <div className="text-xs font-mono text-blue-300">
                  {project.tech}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </Section>

      {/* Contact */}
      <Section id="contact" className="text-center pb-32">
        <h2 className="text-4xl font-bold text-white mb-6">Get In Touch</h2>
        <p className="text-gray-400 max-w-lg mx-auto mb-10">
          I am currently looking for new opportunities. Whether you have a
          question or just want to say hi, my inbox is always open.
        </p>
        <a
          href={`mailto:${profileData.email}`}
          className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border border-blue-500 text-blue-500 hover:bg-blue-500/10 rounded-lg font-medium transition-all"
        >
          <Mail size={20} />
          Say Hello
        </a>
      </Section>

      {/* Footer */}
      <footer className="text-center py-8 text-gray-600 text-sm">
        <p>Designed & Built by {profileData.name}</p>
      </footer>
    </div>
  );
}
